<!--
  ApprovalStatePill.vue -- one approval state, rendered once.

  Card: Decoration color + family model (https://trello.com/c/2hcgDM7J)
  Handoff: planning/design-handoffs/handoff_2026-08-23/
           2026-08-23-decoration-family-model/
  ADR 0067 / Locked Decision 13 -- four values, and only four.

  The whole visual vocabulary of approval on this page lives here: the
  four states' colours and their wording. A second component that
  styled `changes` differently would let one surface look more
  reassuring than another about the identical fact, so the pill is a
  leaf with no logic beyond mapping a state to a class.

  It takes an already-resolved UI state (one of the APPROVAL_*
  constants from `utils/decorationFamilySelectors`), never a raw
  backend status -- the translation happens once, in the selector, so
  an unrecognised status cannot resolve to a different word here than
  it does in the accordion.
-->
<template>
    <span
        class="asp"
        :class="`asp--${state}`"
        :title="clientName ? `${clientName} -- ${label}` : label"
    >
        <span class="asp__dot" aria-hidden="true" />
        <span v-if="clientName" class="asp__client">{{ clientName }}</span>
        <!--
          The state is ALWAYS written, never carried by colour alone.
          The prototype leaves it to the swatch plus a hover title,
          which is invisible to a screen reader, to a keyboard user and
          in a screenshot -- and this is the fact the whole page exists
          to communicate.
        -->
        <span class="asp__state">{{ label }}</span>
    </span>
</template>

<script>
import {
    APPROVAL_NOT_SENT,
    APPROVAL_AWAITING,
    APPROVAL_CHANGES,
    APPROVAL_APPROVED,
    APPROVAL_LABELS,
} from "@/utils/decorationFamilySelectors";

export default {
    name: "ApprovalStatePill",
    props: {
        /** One of the four APPROVAL_* UI states. */
        state: {
            type: String,
            required: true,
            validator: (v) =>
                [
                    APPROVAL_NOT_SENT,
                    APPROVAL_AWAITING,
                    APPROVAL_CHANGES,
                    APPROVAL_APPROVED,
                ].includes(v),
        },
        /**
         * When set, the pill is attributed to a client and reads
         * "<client> <state>" -- the per-size grain, where the question
         * is "where does each client stand on THIS size?". When unset
         * the pill reads as the state alone, which is the accordion's
         * grain (the client is already the row it sits in).
         *
         * Either way the state itself is always written out: colour
         * alone is not a readout.
         */
        clientName: {
            type: String,
            default: "",
        },
    },
    computed: {
        label() {
            return APPROVAL_LABELS[this.state] ?? APPROVAL_LABELS.not_sent;
        },
    },
};
</script>

<style scoped>
.asp {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: var(--tf-radius-pill);
    font-size: 11.5px;
    font-weight: 500;
    max-width: 190px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border: 1px solid transparent;
}
.asp__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: currentColor;
}
.asp__client {
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 600;
}
/* The state reads as a quieter clause after the client's name so the
   pill scans as "who -- what", not as two competing labels. */
.asp__client + .asp__state::before {
    content: " ";
}
.asp__state {
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: 0.85;
}

/* Approved is the quietest of the four on purpose: the strip and the
   accordion lead with what needs the operator, so approval should not
   be the loudest thing on the row. */
.asp--approved {
    background: var(--tf-chip-success-bg);
    color: var(--tf-success-dark);
    border-color: var(--tf-chip-success-border);
}
.asp--changes {
    background: var(--tf-chip-error-bg);
    color: var(--tf-chip-error-fg);
    border-color: var(--tf-chip-error-border);
}
.asp--awaiting {
    background: var(--tf-chip-pending-bg);
    color: var(--tf-chip-pending-fg);
    border-color: var(--tf-chip-pending-border);
}
.asp--not_sent {
    background: var(--tf-chip-neutral-bg);
    color: var(--tf-fg-3);
    border-color: var(--tf-chip-neutral-border);
}
</style>
